import { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Table,
  Typography,
} from "antd";
import {
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  createAsset,
  retrieveAssets,
  selectAsset,
  deleteAsset,
  updateAsset,
} from "../../store/Assets/AssetSlice.js";
import { getJwtToken } from "../../utilities/authentication";
import { useCrudAccess } from "../../hooks/usePageAccess";
import useTranslation from "../../localization/useTranslation";
import appToast from "../../utilities/appToast";

const Assets = () => {
  let { assets, loading, selectedAsset, error, successMessage, total } =
    useSelector((state) => state.assets);
  const [edit, setEdit] = useState(false);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { canList, canCreate, canEdit, canDelete } = useCrudAccess("assets");
  const { t } = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchName, setSearchName] = useState("");
  const [searchNotes, setSearchNotes] = useState("");
  const searchRef = useRef({ name: "", notes: "" });

  const assetRows = (Array.isArray(assets) ? assets : []).map((asset) => {
    return { ...asset, key: asset.id };
  });

  const columns = [
    {
      title: t("assets.table.name"),
      dataIndex: "name",
    },
    {
      title: t("assets.table.cost"),
      dataIndex: "cost",
      render: (value, record) => (
        <Typography.Text>
          {Number(value).toFixed(2)} {record.currency}
        </Typography.Text>
      ),
    },
    {
      title: t("assets.table.notes"),
      dataIndex: "notes",
    },
  ];

  if (canEdit || canDelete) {
    columns.push({
      title: t("assets.table.actions"),
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <Row gutter={3}>
          {canEdit && (
            <Col>
              <Button
                icon={<EditOutlined />}
                type="primary"
                onClick={() => editModal(record)}
              >
                {t("common.edit")}
              </Button>
            </Col>
          )}
          {canDelete && (
            <Col>
              <Popconfirm
                title={t("assets.confirm.deleteTitle")}
                description={t("assets.confirm.deleteMessage")}
                onConfirm={() => handleDeleteAsset(record.id)}
                okText={t("common.deleteConfirm")}
                cancelText={t("common.back")}
              >
                <Button icon={<DeleteOutlined />} type="primary" danger>
                  {t("common.delete")}
                </Button>
              </Popconfirm>
            </Col>
          )}
        </Row>
      ),
    });
  }

  const handleFinish = (values) => {
    if (edit && !canEdit) {
      return;
    }

    if (!edit && !canCreate) {
      return;
    }

    edit ? handleUpdateAsset(values) : handleAddAsset(values);
    setOpen(false);
  };

  const handleAddAsset = (values) => {
    if (!canCreate) {
      return;
    }

    const obj = {
      values,
      token: getJwtToken(),
    };

    dispatch(createAsset(obj));
  };

  const handleUpdateAsset = (values) => {
    if (!canEdit) {
      return;
    }

    values = {
      ...values,
      id: selectedAsset.id,
    };

    dispatch(
      updateAsset({
        payload: values,
        token: getJwtToken(),
      }),
    );
  };

  const handleDeleteAsset = (id) => {
    if (!canDelete) {
      return;
    }

    dispatch(deleteAsset({ id }));
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const initFetch = useCallback(
    (page = 1, per_page = 10, name = "", notes = "") => {
      if (!canList) {
        return;
      }

      const token = getJwtToken();
      const params = { page, per_page };
      if (name) params.name = name;
      if (notes) params.notes = notes;

      dispatch(retrieveAssets({ ...params, token }));
    },
    [dispatch, canList],
  );

  useEffect(() => {
    initFetch(
      currentPage,
      pageSize,
      searchRef.current.name,
      searchRef.current.notes,
    );
  }, [initFetch, currentPage, pageSize]);

  useEffect(() => {
    if (successMessage) {
      appToast.success(successMessage);
    }
  }, [successMessage]);

  useEffect(() => {
    const t = setTimeout(() => {
      // reset to page 1 when filters change
      setCurrentPage(1);
      initFetch(1, pageSize, searchName, searchNotes);
      searchRef.current = { name: searchName, notes: searchNotes };
    }, 400);
    return () => clearTimeout(t);
  }, [searchName, searchNotes, initFetch, pageSize]);

  if (!canList) {
    return (
      <Alert message={t("assets.alert.noAccess")} type="warning" showIcon />
    );
  }

  const handleAddModal = () => {
    if (!canCreate) {
      return;
    }

    form.setFieldsValue({
      name: "",
      cost: 0,
      notes: "",
    });
    setEdit(false);
    setOpen(true);
  };

  const editModal = (record) => {
    if (!canEdit) {
      return;
    }

    form.setFieldsValue({
      ...record,
    });
    dispatch(selectAsset(record));
    setEdit(true);
    setOpen(true);
  };

  const modalUI = () => {
    return (
      <Modal
        title={t("assets.modal.title")}
        open={open}
        onCancel={handleCancel}
        footer={[
          <Button key="Cancel" onClick={handleCancel}>
            {t("common.cancel")}
          </Button>,
        ]}
      >
        <Form
          form={form}
          onFinish={handleFinish}
          dir="rtl"
          labelCol={{ span: 24 }}
        >
          <Form.Item
            name="name"
            label={t("assets.form.name.label")}
            rules={[
              {
                required: true,
                message: t("assets.form.name.required"),
              },
            ]}
          >
            <Input placeholder={t("assets.form.name.placeholder")} />
          </Form.Item>

          <Form.Item
            name="cost"
            label={t("assets.form.cost.label")}
            rules={[
              {
                required: true,
                message: t("assets.form.cost.required"),
              },
            ]}
          >
            <InputNumber placeholder={t("assets.form.cost.placeholder")} />
          </Form.Item>

          <Form.Item name="notes" label={t("assets.form.notes.label")}>
            <Input.TextArea placeholder={t("assets.form.notes.placeholder")} />
          </Form.Item>

          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={loading}>
              {edit ? t("common.edit") : t("common.add")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };
  return (
    <div>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      {modalUI()}
      {canCreate && (
        <Row gutter={16} style={{ paddingBottom: "20px", paddingTop: "20px" }}>
          <Col>
            <Button
              icon={<PlusCircleOutlined />}
              type="primary"
              onClick={handleAddModal}
            >
              {t("assets.button.add")}
            </Button>
          </Col>
        </Row>
      )}

      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col>
          <Input.Search
            placeholder={t("assets.search.namePlaceholder") || "Search by name"}
            allowClear
            onSearch={(v) => setSearchName(v)}
            onChange={(e) => setSearchName(e.target.value)}
            style={{ width: 300 }}
            value={searchName}
          />
        </Col>
        <Col>
          <Input.Search
            placeholder={
              t("assets.search.notesPlaceholder") || "Search by notes"
            }
            allowClear
            onSearch={(v) => setSearchNotes(v)}
            onChange={(e) => setSearchNotes(e.target.value)}
            style={{ width: 300 }}
            value={searchNotes}
          />
        </Col>
      </Row>

      <Table
        loading={loading}
        columns={columns}
        dataSource={assetRows}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "15", "20", "50"],
        }}
        onChange={(pagination) => {
          if (pagination && pagination.current)
            setCurrentPage(pagination.current);
          if (pagination && pagination.pageSize)
            setPageSize(pagination.pageSize);
        }}
      />
    </div>
  );
};

export default Assets;
